// Query hook for GET /api/v1/admin/feed/editorial/{id} — the full
// editable fields (title, full body, urls, tags) that seed the
// cockpit's edit mode. The list endpoint only carries a 200-char body
// excerpt, hence the dedicated detail read.

import { useQuery, type UseQueryResult } from "@tanstack/react-query";

import { attachRequestIdToScope } from "../../lib/api-client";
import { ADMIN_EDITORIAL_QUERY_KEY } from "./useCreateEditorialPost";

export interface EditorialDetail {
  id: string;
  title: string;
  body: string;
  media_url: string | null;
  external_url: string | null;
  tags: string[];
  published_at: string;
  status: string;
  moderation_state: string;
}

async function fetchEditorialDetail(id: string): Promise<EditorialDetail> {
  const response = await fetch(
    `/api/v1/admin/feed/editorial/${encodeURIComponent(id)}`,
    { credentials: "include" },
  );
  attachRequestIdToScope(response);
  if (!response.ok) {
    throw new Error(`editorial detail fetch failed (HTTP ${response.status})`);
  }
  const raw = (await response.json()) as { data: EditorialDetail };
  return raw.data;
}

export function useEditorialDetail(
  id: string | null,
): UseQueryResult<EditorialDetail, Error> {
  return useQuery({
    queryKey: [...ADMIN_EDITORIAL_QUERY_KEY, "detail", id],
    queryFn: () => fetchEditorialDetail(id as string),
    enabled: !!id,
    staleTime: 0,
    retry: false,
  });
}
